import theme from 'src/shared/theme';
import styled, { css } from 'styled-components';
import { Icon } from '../icon';

export const APP_SIDEBAR_WIDTH = 295;
export const APP_SIDEBAR_COLLAPSED_WIDTH = 64;

export const tint = (hex: string, amount: number) => {
    let R = parseInt(hex.substring(1, 3), 16);
    let G = parseInt(hex.substring(3, 5), 16);
    let B = parseInt(hex.substring(5, 7), 16);

    const getSingle = (number: number) => parseInt(((number * (100 + amount)) / 100).toFixed(0));

    R = getSingle(R);
    G = getSingle(G);
    B = getSingle(B);

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    const getDouble = (number: number) => (number.toString(16).length === 1 ? `0${number.toString(16)}` : number.toString(16));

    const RR = getDouble(R);
    const GG = getDouble(G);
    const BB = getDouble(B);

    return `#${RR}${GG}${BB}`;
};

export const Main = styled.nav<{ $isCollapsed?: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    height: 100%;
    width: ${(props) => (props.$isCollapsed ? APP_SIDEBAR_COLLAPSED_WIDTH : APP_SIDEBAR_WIDTH)}px;

    background-color: rgb(0 0 0 / 4%);

    display: flex;
    flex: 1;
    flex-direction: column;

    overflow: hidden;
    z-index: 18;
    transition: width 0.2s ease;
`;

export const SidebarContent = styled.div<{ $isCollapsed?: boolean }>`
    width: ${(props) => (props.$isCollapsed ? APP_SIDEBAR_COLLAPSED_WIDTH : APP_SIDEBAR_WIDTH)}px;
    min-width: ${(props) => (props.$isCollapsed ? APP_SIDEBAR_COLLAPSED_WIDTH : APP_SIDEBAR_WIDTH)}px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
`;

//-------------------------------------------
// Logo + Home live inside the sidebar with the same grey background.
export const Header = styled.div<{ $isCollapsed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.$isCollapsed ? 'center' : 'space-between')};
    gap: ${(props) => (props.$isCollapsed ? '2px' : '8px')};
    padding: ${(props) => (props.$isCollapsed ? '8px 0' : '8px 12px')};
    height: 48px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    flex-shrink: 0;
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
    flex-shrink: 0;
`;

export const HeaderLogoLink = styled.a`
    display: flex;
    align-items: center;
    cursor: pointer;
    min-width: 0;
    flex: 0 1 auto;

    svg {
        height: 26px;
        width: auto;
        max-width: 100%;
        display: block;
    }
`;

export const HeaderHomeLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    cursor: pointer;
    text-decoration: none;
    color: #4a4a4a;
    flex-shrink: 0;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        background: rgb(0 0 0 / 5%);
        color: ${theme.app.secondary};
    }

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
`;

export const HeaderCollapseButton = styled.button<{ $isCollapsed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    color: #4a4a4a;
    flex-shrink: 0;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        background: rgb(0 0 0 / 5%);
        color: ${theme.app.secondary};
    }

    ${Icon} {
        width: 20px;
        height: 20px;
        min-width: 20px;
        transform: scaleX(${(props) => (props.$isCollapsed ? '-1' : '1')});
        transition: transform 0.2s ease;
    }
`;

export const CollapsedNavigation = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 0 12px 0;
    overflow-x: hidden;
    overflow-y: auto;

    ::-webkit-scrollbar {
        width: 3px;
    }

    ::-webkit-scrollbar-track {
        background: transparent;
    }

    ::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 20px;
    }

    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    scrollbar-width: thin;
`;

export const CollapsedNavigationLink = styled.a<{ $isSelected?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    color: ${(props) => (props.$isSelected ? theme.app.secondary : '#4a4a4a')};
    background: ${(props) => (props.$isSelected ? 'rgb(0 0 0 / 6%)' : 'transparent')};
    cursor: pointer;
    text-decoration: none;
    flex-shrink: 0;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        background: rgb(0 0 0 / 5%);
        color: ${theme.app.secondary};
    }

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
        stroke: none;
    }

    span {
        color: inherit;
        font-size: 16px;
        line-height: 1;
    }
`;

export const HeaderMenu = styled.div`
    position: relative;
`;

export const Body = styled.div`
    display: flex;
    flex: 1px;
    min-height: 0;
`;

//-------------------------------------------
export const ContainerLeft = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
`;

export const ContainerRight = styled.nav<{ $numOfShadow: number }>`
    /* Per togliere il bordino */
    border-left: ${(props) => (props.$numOfShadow == 0 ? `solid 2px ${theme.app.secondary_dark}` : null)};

    background-color: ${(props) => tint(theme.app.backgroundColor, props.$numOfShadow * -4)};
`;

//-------------------------------------------
export const ContainerLeftTop = styled.nav`
    width: 100%;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    position: relative;
    padding: 6px 0 0 0;

    ::-webkit-scrollbar {
        width: 3px;
    }
    ::-webkit-scrollbar-track {
        background: ${theme.app.backgroundColor};
        border-radius: 20px;
    }
    ::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 20px;
    }

    scrollbar-color: rgba(0, 0, 0, 0.2) ${theme.app.backgroundColor};
    scrollbar-width: thin;
`;

export const ContainerLeftBottom = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: end;
`;

export const SidebarBottomUtilities = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 12px;
`;

// Search & Help placed under the SolutionSwitcher at the top of the sidebar.
// Horizontal margin matches the switcher (12px) so they align.
export const SidebarTopSearch = styled.div`
    margin: 0 12px 12px;
`;

//-------------------------------------------
export const NavbarBottom = styled.nav`
    color: ${theme.app.primary};
    font-weight: ${theme.font.semi_bold};
    font-size: 14px;

    padding-top: 30px;
`;

export const Trial = styled.div<{ $isTrial: boolean }>`
    color: ${theme.app.primary};
    font-weight: ${theme.font.semi_bold};
    font-size: 12px;
    display: flex;
`;

//-------------------------------------------
export const NavbarContainer = styled.div`
    position: relative;
`;

export const Navbar = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    box-sizing: border-box;
    list-style-type: none;
    padding-inline-start: 1px;
    display: flex;
    flex-direction: column;
    gap: 1px;
    height: 100%;
    margin: 0;
`;

export const NavbarUlSmall = styled.ul`
    list-style-type: none;
    padding: 0px;
    margin: 0px;
    display: flex;
    flex: 1;
    flex-direction: column;
`;

//-------------------------------------------
export const NavbarLi = styled.li<{ $isVisible: boolean }>`
    position: relative;
    ${(props) => (props.$isVisible ? null : 'display: none')};
    border-radius: 6px;
    transition: background 0.15s ease;

    &:hover {
        background: rgb(0 0 0 / 5%);

        .sidebar-view-icon {
            opacity: 1 !important;
        }
    }
`;

export const NavbarLiSmall = styled.li<{ $numOfShadow: number }>`
    background-color: ${(props) => tint(theme.app.backgroundColor, props.$numOfShadow * -4)};
`;

export const Linkcss = css`
    padding: 6px 12px;
    display: flex;
    align-items: center;
    justify-content: left;
    text-decoration: none;
    cursor: pointer;
    color: #4a4a4a;
`;

//-----------------------------------------------
export const LinkSidebar = styled.a<{ $isHeader?: boolean; $actionsOffset?: number }>`
    /* height: 25px; */
    ${Linkcss}
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding-right: ${(props) => props.$actionsOffset ?? 24}px;
    ${(props) => (props.$isHeader ? `font-weight: 600;` : `font-weight: ${theme.font.regular};`)};
    ${(props) => (props.$isHeader ? `font-size: 11px;` : `font-size: 12px;`)};
    ${(props) => (props.$isHeader ? `text-transform: uppercase; letter-spacing: 0.8px;` : null)};
    ${(props) => (props.$isHeader ? 'min-height: 36px;' : 'min-height: 30px;')};
    ${(props) => (props.$isHeader ? 'padding-bottom: 12px;' : null)};
    ${(props) => (props.$isHeader ? 'cursor: default;' : 'cursor: pointer;')};

    > span:first-of-type {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const LinkHeaderMenu = styled.a`
    display: flex;
    padding: 8px 0px 8px 8px;
    margin: 0px;
    align-items: center;
    justify-content: left;
    text-decoration: none;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    cursor: pointer;

    background-color: ${theme.app.secondary_alt};
    color: ${theme.app.backgroundColor};
    font-weight: 600;
`;

// export const LinkHeader = styled(Link)`
//     font-weight: ${theme.font.semi_bold};
// `;

export const Link_small = styled.a<{ $isHeader?: boolean }>`
    width: 33px;
    height: 57px;
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s ease;

    /* padding: 13px 4px;  per icona normale */

    ${(props) =>
        props.$isHeader ? 'padding: 18px 4px 8px 4px;' : 'padding: 13px 4px;'} /* spostato un pò in basso per la freccia...togliere per icona normale  */

    &:hover {
        background: rgb(0 0 0 / 5%);
    }
`;

// ---------------------------------------------
type ContainerIconProps = {
    $isSelected?: boolean;
    $color?: string;
};

export const ContainerIcon = styled.div<ContainerIconProps>`
    width: 16px;
    height: 16px;

    min-width: 16px;
    min-height: 16px;
    margin-right: 10px;
    color: ${(props) => (props.$isSelected ? theme.app.secondary : (props.$color ?? '#4a4a4a'))};
    svg {
        width: 16px;
        height: 16px;
        stroke: none;
        fill: ${(props) => (props.$isSelected ? theme.app.secondary : (props.$color ?? '#4a4a4a'))};
    }
    span {
        color: ${(props) => (props.$isSelected ? theme.app.secondary : (props.$color ?? '#4a4a4a'))};
    }
`;

export const ContainerIconHelp = styled(ContainerIcon)`
    > svg {
        fill: ${theme.app.backgroundColor};
    }
`;

export const ContainerIconSmall = styled.div<{ $isHeader?: boolean }>`
    width: 25px;
    height: 25px;

    > svg {
        /* fill: ${theme.app.primary};  */
        ${(props) => (props.$isHeader ? `fill: ${theme.app.secondary};` : `fill: ${theme.app.primary};`)}
    }
`;

export const ContainerArrow = styled.div<{ $isHidden?: boolean }>`
    width: 13px;
    height: 13px;
    position: absolute;
    top: 6px;
    right: 3px;
    ${(props) => (props.$isHidden ? 'visibility: visible;' : 'visibility: hidden;')}
    svg {
        stroke: white;
    }
`;

// Tooltip
export const ContainerTooltip = styled.div`
    position: relative;
    width: 16px;
    height: 16px;

    min-width: 16px;
    min-height: 16px;
    margin-left: 7px;

    svg {
        width: 16px;
        height: 16px;
    }
`;
export const TitleAndTooltipContainer = styled.div`
    display: grid;
    align-items: center;
    grid-template-columns: max-content 15px;
    gap: 5px;
    max-height: 15px;
    div {
        height: 15px;
        width: 15px;
    }
    svg {
        height: 15px;
        width: 15px;
        color: ${(props) => props.theme.app.secondary};
    }
`;
export const ContainerTooltipIcon = styled.div`
    position: absolute;
    top: 0px;
    width: 16px;
    height: 16px;
`;

export const TooltipStyled = styled.div`
    background-color: red;
`;

// Sezione Subscription ________________________________________________
export const TextDayTrial = styled.div`
    padding: 3px;

    color: ${theme.app.primary};
    font-weight: ${theme.font.bold};
    font-size: 40px;
    text-transform: uppercase;
`;

export const ContainerTextSubscribe = styled.div`
    display: flex;
    padding: 5px;
    flex-direction: column;
    justify-content: center;
`;

export const ContainerSubscription = styled.div`
    display: flex;
    cursor: pointer;
    align-items: center;
`;

export const TextSubscribe = styled.text`
    color: ${theme.app.secondary};
    font-weight: ${theme.font.bold};
    font-size: 13px;
`;

export const ImgArrowRight = styled.div`
    margin-left: 5px;
`;

export const ContainerConfigurator = styled.div`
    position: relative;
    background-color: ${theme.app.secondary};
    color: ${theme.app.backgroundColor};
    padding: 17px 38px 17px 15px;

    font-weight: 600;
    font-size: 14px;
    text-transform: uppercase;
    cursor: pointer;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ImgArrowRightWhite = styled.div`
    width: 20px;
    height: 20px;
    padding-left: 8px;
    position: absolute;
    top: 20px;
    right: 20px;
`;

// Banner Composer
export const ContanierComposerBanner = styled.div`
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    background-color: ${theme.app.secondary};
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    z-index: 11;
    height: 51px;
    /* text-transform: uppercase; */

    color: ${theme.app.backgroundColor};
    font-weight: ${theme.font.bold};
    font-size: 14px;
`;

export const ImgArrowRightWhiteComposer = styled.div`
    padding: 0px 10px;
`;

export const SwitchComposer = styled.div`
    border: none;
    border-radius: 30px;
    padding: 12px 40px;
    text-transform: uppercase;
    justify-content: center;
    cursor: pointer;

    background-color: ${theme.app.backgroundColor};
    color: ${theme.app.secondary};
    font-weight: 600;
    font-size: 14px;
`;

export const LinkComposer = styled.a`
    /* background-color: ${theme.app.backgroundColor}; */
    color: ${theme.app.backgroundColor};
    font-weight: ${theme.font.bold};
    font-size: 14px;
    padding-left: 20px;
    text-decoration: underline;
`;

export const CloseComposerBanner = styled.div`
    width: 25px;
    height: 25px;
    padding-left: 8px;
    position: absolute;
    top: 18px;
    right: 25px;
    cursor: pointer;

    > svg {
        fill: ${theme.app.backgroundColor};
    }
`;

// ── Menu category (collapsible section) styles ──────────────────────
// Arrow sits inline right after the label and is revealed only on hover,
// so the idle sidebar is clean; padding and hover behaviour mirror
// LinkSidebar so the whole sidebar reads as a single coherent list.
export const MenuSectionArrow = styled.span<{ $isOpen: boolean }>`
    display: inline-block;
    flex-shrink: 0;
    margin-left: 6px;
    font-size: 8px;
    color: inherit;
    opacity: 0;
    transition:
        transform 0.2s ease,
        opacity 0.15s ease;
    transform: rotate(${(props) => (props.$isOpen ? '90deg' : '0deg')});
`;

export const MenuSectionIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    flex-shrink: 0;
    color: inherit;

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
        stroke: none;
    }

    span {
        color: inherit;
        font-size: 16px;
    }
`;

export const MenuSectionHeader = styled.div<{ $isSelected?: boolean; $isStatic?: boolean; $isUppercase?: boolean }>`
    margin-top: ${(props) => (props.$isUppercase ? '4px' : '0')};
    padding: ${(props) => (props.$isUppercase ? '8px 12px' : '6px 12px')};
    width: 100%;
    min-width: 0;
    min-height: ${(props) => (props.$isUppercase ? 'auto' : '30px')};
    box-sizing: border-box;
    font-size: ${(props) => (props.$isUppercase ? '11px' : '12px')};
    font-weight: ${(props) => (props.$isUppercase ? '600' : theme.font.regular)};
    text-transform: ${(props) => (props.$isUppercase ? 'uppercase' : 'none')};
    letter-spacing: ${(props) => (props.$isUppercase ? '0.8px' : '0')};
    color: ${(props) => (props.$isSelected ? theme.app.secondary : props.$isUppercase ? theme.app.primary : '#4a4a4a')};
    cursor: ${(props) => (props.$isStatic ? 'default' : 'pointer')};
    display: flex;
    align-items: center;
    user-select: none;
    border-radius: 6px;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        background: ${(props) => (props.$isStatic ? 'transparent' : 'rgb(0 0 0 / 5%)')};
    }

    &:hover ${MenuSectionArrow} {
        opacity: 0.7;
    }

    ${MenuSectionIcon} {
        margin-right: ${(props) => (props.$isUppercase ? '8px' : '10px')};
    }

    > span:not(${MenuSectionIcon}):not(${MenuSectionArrow}) {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const MenuSectionBody = styled.div<{ $collapsed: boolean }>`
    display: ${(props) => (props.$collapsed ? 'none' : 'flex')};
    flex-direction: column;
    gap: 2px;
    padding-left: 24px;
`;

export const MenuBottomDivider = styled.div`
    margin-top: 18px;
    padding-top: 10px;
    border-top: 1px dotted rgba(0, 0, 0, 0.12);
`;

// Thin separator between sidebar sections (top items / Quick Access / rest).
export const MenuDivider = styled.div`
    height: 1px;
    margin: 8px 12px;
    background-color: rgba(0, 0, 0, 0.08);
`;

// Star toggle shown at the right edge of a leaf menu item. Hidden until the
// row is hovered (via the shared `.sidebar-view-icon` reveal on NavbarLi),
// regardless of whether the page is starred — the filled vs outline icon and
// the colour convey the state once revealed.
export const StarToggle = styled.button<{ $active?: boolean }>`
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${(props) => (props.$active ? '#f5a623' : '#9ca3af')};
    opacity: 0;
    transition:
        opacity 0.15s ease,
        color 0.15s ease;

    &:disabled {
        cursor: default;
    }

    &:hover {
        color: ${(props) => (props.$active ? '#e0951c' : '#f5a623')};
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 16px;
    }

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

export const StarredDragHandle = styled.button`
    position: absolute;
    right: 28px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: grab;
    color: #9ca3af;
    opacity: 0;
    touch-action: none;
    transition:
        opacity 0.15s ease,
        color 0.15s ease;

    &:active {
        cursor: grabbing;
    }

    &:hover {
        color: #4a4a4a;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 16px;
    }

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

export const StarredSortableItemContainer = styled.div<{ $isDragging?: boolean }>`
    position: relative;
    z-index: ${(props) => (props.$isDragging ? 2 : 'auto')};
    opacity: ${(props) => (props.$isDragging ? 0.75 : 1)};
`;
